import { useCallback, useMemo, useRef } from 'react'

// Corrects this machine's clock against Firestore's.
//
// Claim expiry and peer staleness are comparisons between a server timestamp
// and "now". "Now" is local, so a moderator whose laptop is two minutes fast
// would declare every peer dead, and one two minutes slow would never reclaim
// an abandoned video. Neither is hypothetical on shared event hardware.
//
// The offset is measured from this session's own heartbeat: note the local time
// around the write, then compare it against the value the listener reads back
// once the server has materialized it. The write's midpoint is used rather than
// its start, which halves the round-trip bias — irrelevant against thresholds of
// tens of seconds, but free. Samples are kept and the median taken, so one slow
// round-trip does not move the estimate.

const SAMPLE_COUNT = 5

export function useServerClock() {
  const state = useRef({ offset: 0, samples: [], writes: [], echoes: [], lastEcho: null })

  // Writes and echoes are paired in order rather than by "the last one wins":
  // the listener can deliver a materialized heartbeat before its own write
  // promise resolves, and pairing an echo with a later write would put a whole
  // heartbeat interval of error into the offset.
  const drain = () => {
    const st = state.current
    while (st.writes.length && st.echoes.length) {
      const localMs = st.writes.shift()
      const serverMs = st.echoes.shift()
      st.samples = [...st.samples, serverMs - localMs].slice(-SAMPLE_COUNT)
      const sorted = [...st.samples].sort((a, b) => a - b)
      st.offset = sorted[Math.floor(sorted.length / 2)]
    }
    // Neither queue should grow: a dropped write or a snapshot we never wrote
    // would otherwise skew every later pairing.
    if (st.writes.length > SAMPLE_COUNT) st.writes = st.writes.slice(-1)
    if (st.echoes.length > SAMPLE_COUNT) st.echoes = st.echoes.slice(-1)
  }

  /** Local time attributed to a heartbeat write, called with (sentAt+ackAt)/2. */
  const noteWrite = useCallback((localMs) => {
    state.current.writes.push(localMs)
    drain()
  }, [])

  /** The server's own timestamp for that write, once it comes back. */
  const noteEcho = useCallback((serverMs) => {
    if (!serverMs || serverMs === state.current.lastEcho) return
    state.current.lastEcho = serverMs
    state.current.echoes.push(serverMs)
    drain()
  }, [])

  // Before the first sample this is Date.now(), which is right for a machine
  // with a sane clock and self-corrects within one heartbeat otherwise.
  const serverNow = useCallback(() => Date.now() + state.current.offset, [])

  const offset = useCallback(() => state.current.offset, [])

  // Stable identity matters more than it looks: the coordination tick closes
  // over this object, so a fresh one each render would re-create the tick, and
  // re-creating the tick re-installs its interval and fires it immediately —
  // once per render instead of once per second.
  return useMemo(
    () => ({ serverNow, noteWrite, noteEcho, offset }),
    [serverNow, noteWrite, noteEcho, offset],
  )
}
